import { useLikeStructureMutation } from "@ac-expo/api";
import {
  OpenExponentConversationEvent,
  OpenExponentSocialsEvent,
  OpenExponentWebsiteEvent,
  trackedEvents,
} from "@ac-expo/api-types";
import { useToast } from "@ac-expo/ui-components";
import { useUserContext } from "@web-app/src/context/auth/user.context";
import AuthStore from "@web-app/src/services/AuthStore";
import { useRouter } from "next/router";

type GetAppEventClickHandler = (
  appEvent: (
    | OpenExponentConversationEvent
    | OpenExponentWebsiteEvent
    | OpenExponentSocialsEvent
  )["name"]
) => () => void;

type OnEventParticipationCardClick = (
  eventId: string,
  eventSlug: string | null
) => void;

type UseExhibitorActionsOptions = {
  structureId: string;
  structureAdminId?: string;
  isLiked?: boolean;
  getAppEventClickHandler?: GetAppEventClickHandler;
  onEventParticipationCardClick?: OnEventParticipationCardClick;
  isEmbedded?: boolean;
};

export const useExhibitorActions = ({
  structureId,
  structureAdminId,
  isLiked,
  getAppEventClickHandler = () => () => {},
  onEventParticipationCardClick,
  isEmbedded = false,
}: UseExhibitorActionsOptions) => {
  const { toast } = useToast();
  const router = useRouter();
  const { mutate: likeStructure } = useLikeStructureMutation();
  const { isAuthenticated } = useUserContext();

  const toggleLike = () => {
    if (!isAuthenticated) {
      AuthStore.setModalVisible(true);
      return;
    }

    const newFavoriteStatus = !isLiked;
    likeStructure(
      { structureId, like: newFavoriteStatus },
      {
        onSuccess: () => {
          toast({
            title: newFavoriteStatus ? "Favoris ajouté" : "Favoris supprimé",
          });
        },
      }
    );
  };

  const onWebsiteClick = getAppEventClickHandler(
    trackedEvents.openExponentWebsite
  );

  const onSocialsClick = getAppEventClickHandler(
    trackedEvents.openExponentSocials
  );

  const onConversationClick = () => {
    getAppEventClickHandler(trackedEvents.openExponentConversation)();
    if (structureAdminId) {
      router.push(`/conversations?participantId=${structureAdminId}`);
    }
  };

  const openEventDetails = (eventId: string, eventSlug: string | null) => {
    if (onEventParticipationCardClick) {
      onEventParticipationCardClick(eventId, eventSlug);
    }

    if (!isEmbedded && eventSlug) {
      router.push(`/event/${eventSlug}`);
    }
  };

  return {
    toggleLike,
    onWebsiteClick,
    onSocialsClick,
    onConversationClick,
    openEventDetails,
  };
};
